import {
  trackAcceptAndContinueGBPPaymentWithFee,
  trackChangeCountryOfCardIssuance,
} from 'actions';
import classNames from 'classnames';
import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import { Button } from 'components/ui/Button/Button';
import { Heading } from 'components/ui/Heading/Heading';
import { Spinner } from 'components/ui/Spinner/Spinner';
import { SURCHARGE_COUNTRIES_CUSTOM_WARNING } from 'constants/countries';
import { useDispatch, useSelector } from 'react-redux';
import { getSenderCountry } from 'selectors/entities/order/order';
import { amountFrom, getTotalAmount } from 'selectors/ui/ui';
import { useTranslations } from 'utils';
import { createMarkup } from 'utils/createMarkup/createMarkup';

import './Surcharge.scss';

type SurchargeProps = {
  feeRate: number;
  productType: string;
  onAcceptFee: () => void;
  onChangeCountry: () => void;
};

const Surcharge = ({
  feeRate,
  productType,
  onAcceptFee,
  onChangeCountry,
}: SurchargeProps) => {
  const i18n = useTranslations();
  const country = useSelector(getSenderCountry);
  const amount = useSelector(getTotalAmount);
  const dispatch = useDispatch();

  const getCountryWarning = () => {
    if (SURCHARGE_COUNTRIES_CUSTOM_WARNING.includes(country)) {
      return `surcharge.confirm.description.${country.toLowerCase()}`;
    }

    return 'surcharge.confirm.description';
  };

  const handleClick = (): void => {
    dispatch(trackChangeCountryOfCardIssuance({ productType }));
    onChangeCountry();
  };

  function handleChargeClick(): void {
    dispatch(trackAcceptAndContinueGBPPaymentWithFee({ productType }));
    onAcceptFee();
  }

  return (
    <div className="Surcharge" data-testid="surcharge">
      <Heading as="h2" size="large" className="fontWeight-semiBold">
        {i18n.t('surcharge.confirm.title')}
      </Heading>
      <div className="Surcharge-description">
        <p
          dangerouslySetInnerHTML={createMarkup(
            i18n.t(getCountryWarning(), {
              feePercentage: ((feeRate || 0) * 100).toFixed(2),
            }),
          )}
        />
      </div>
      <FeeSummary amount={amount} feeRate={feeRate} />
      <footer>
        <Button
          tone="primary"
          size="full"
          onClick={handleClick}
          data-testid="changeSenderCountryBtn"
          disabled={!feeRate}
        >
          {i18n.t('surcharge.changePaymentMethodOrPaymentCountry.button')}
        </Button>
        <button
          onClick={handleChargeClick}
          id="Surcharge-back"
          data-testid="chargeBtn"
          className={classNames('Surcharge-back Link', {
            'Surcharge-back-disabled': !feeRate,
          })}
        >
          {i18n.t('surcharge.accept.button')}
        </button>
      </footer>
    </div>
  );
};

type FeeSummaryProps = {
  amount: number;
  feeRate: number;
};

function FeeSummary({ amount, feeRate }: FeeSummaryProps) {
  const i18n = useTranslations();
  const price = useSelector(amountFrom);

  if (!feeRate) {
    return <Spinner className="FeeSummary-Spinner" />;
  }

  const fee = amount * feeRate;

  return (
    <>
      <dl>
        <dt>{i18n.t('surcharge.originalPaymentAmount.label')}</dt>
        <dd data-testid="paymentAmount">
          <FormattedAmount {...price} />
        </dd>
        <dt data-testid="feePercentage">
          {i18n.t('surcharge.internationalCardFee.label', {
            feePercentage: (feeRate * 100).toFixed(2),
          })}
        </dt>
        <dd data-testid="cardFee">
          <FormattedAmount value={fee} currency={price.currency} />
        </dd>
      </dl>
      <dl>
        <dt>{i18n.t('surcharge.totalPaymentAmount.label')}</dt>
        <dd className="big" data-testid="totalAmount">
          <FormattedAmount
            value={fee + price.value}
            currency={price.currency}
          />
        </dd>
      </dl>
    </>
  );
}

export { Surcharge };
